import { useCallback, useEffect, useRef } from 'react';

/** See `lastCallRef`. Well under the time any real request takes. */
const MIN_CALL_INTERVAL_MS = 300;


/**
 * Load the next page when the end of the list comes near the viewport — the
 * replacement for a "Load more" button.
 *
 * Returns a ref for a sentinel element rendered after the last item.
 *
 * Why this is not just an IntersectionObserver
 * --------------------------------------------
 * An observer only reports CHANGES in intersection. A sentinel that is already
 * on screen when a page finishes loading produces no new callback, so a single
 * missed trigger stalls the list until the user scrolls up and back down —
 * which is what made auto-loading work "sometimes".
 *
 * And one trigger is reliably missed: the observer can fire in the gap between
 * the request finishing and the new pagination reaching state, so `onLoadMore`
 * asks for the page that is already loaded, nothing fetches, and no further
 * callback ever arrives.
 *
 * So the check here is a geometric test — "is the sentinel within `marginPx` of
 * the viewport?" — that can be re-run at any time, and it is run from three
 * places: the observer, every scroll/resize, and after every change to
 * `loading` / `hasMore`. That last one is what makes a tall viewport keep
 * filling itself, and what recovers the race above.
 */
export interface UseInfiniteScrollOptions {
  hasMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  /** How far ahead of the viewport to trigger, in px. Default 600. */
  marginPx?: number;
}

export function useInfiniteScroll({
  hasMore,
  loading,
  onLoadMore,
  marginPx = 600,
}: UseInfiniteScrollOptions) {
  // Read through a ref so the observer and listeners never go stale and never
  // have to be rebuilt as the list grows.
  const stateRef = useRef({ hasMore, loading, onLoadMore, marginPx });
  stateRef.current = { hasMore, loading, onLoadMore, marginPx };

  const nodeRef = useRef<HTMLElement | null>(null);
  /**
   * Floor on how often `onLoadMore` may be called. The geometric check runs on
   * every frame that scrolls, and if a request somehow neither starts nor
   * changes the list (a server insisting on more pages while returning none)
   * the check would otherwise ask again every frame forever.
   */
  const lastCallRef = useRef(0);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const frameRef = useRef<number | null>(null);
  /**
   * Whether a check is already queued. Tracked separately from the frame id:
   * the id is only assigned once requestAnimationFrame RETURNS, so a callback
   * that runs first would have its "I am done" write overwritten by the id and
   * the guard would then reject every later check forever.
   */
  const pendingRef = useRef(false);

  /** Load another page if the sentinel is close enough and nothing is in flight. */
  const check = useCallback(() => {
    const { hasMore: more, loading: busy, onLoadMore: load, marginPx: margin } = stateRef.current;
    const node = nodeRef.current;
    if (!node || !more || busy) return;
    if (typeof window === 'undefined') return;
    const rect = node.getBoundingClientRect();
    // Below the fold by more than the margin: nothing to do yet.
    if (rect.top > window.innerHeight + margin) return;
    const now = Date.now();
    if (now - lastCallRef.current < MIN_CALL_INTERVAL_MS) return;
    lastCallRef.current = now;
    load();
  }, []);

  /** Coalesce bursts of scroll events into one check per frame. */
  const scheduleCheck = useCallback(() => {
    if (pendingRef.current) return;
    pendingRef.current = true;
    frameRef.current = requestAnimationFrame(() => {
      pendingRef.current = false;
      frameRef.current = null;
      check();
    });
  }, [check]);

  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      nodeRef.current = node;
      if (!node) return;

      if (typeof IntersectionObserver !== 'undefined') {
        const observer = new IntersectionObserver(() => scheduleCheck(), {
          rootMargin: `${stateRef.current.marginPx}px`,
        });
        observer.observe(node);
        observerRef.current = observer;
      }
      // The sentinel may already be in view the moment it mounts (a short first
      // page on a tall screen), which no scroll event would ever announce.
      scheduleCheck();
    },
    [scheduleCheck],
  );

  // Scroll / resize are the fallback for everything the observer does not say:
  // passive listeners, one geometric read per frame.
  useEffect(() => {
    window.addEventListener('scroll', scheduleCheck, { passive: true });
    window.addEventListener('resize', scheduleCheck, { passive: true });
    return () => {
      window.removeEventListener('scroll', scheduleCheck);
      window.removeEventListener('resize', scheduleCheck);
    };
  }, [scheduleCheck]);

  // After every load settles — and whenever `hasMore` changes — re-test. If the
  // sentinel is still on screen the list keeps filling; this is the step that
  // makes a missed observer callback harmless.
  useEffect(() => {
    if (!loading && hasMore) scheduleCheck();
  }, [loading, hasMore, scheduleCheck]);

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    },
    [],
  );

  return sentinelRef;
}

export default useInfiniteScroll;
